import { useSelector } from "react-redux";
import { formatArrayText } from "../../utils/functions";
import CheckButton from '../CheckButton';
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Fill, useGameSounds, useMediaQuery, useTranslate, VisualFeedback } from "@educaplay/core";
import useDictationConfig from "../../hooks/useDictationConfig";
import { DICTADO_SOUNDS } from "../../utils/constants";
import { getCorrection } from "../../utils/correction";
import TextInput from "../TextInput";
import CorrectionSummary from "../CorrectionSummary";
import { motion, useAnimate } from "framer-motion";
import { wait } from "../../utils/promises";
import { setAnimating } from "@educaplay/store/slices/gameSlice";
import { useDispatch } from "react-redux";
import { useEffect } from "react";


const DELAY_SOUND_CHARGE_NOTE = 400;
const DELAY_SOUND_CHARGE_BUTTON = 200;
export default forwardRef(function NoteController(props, ref) {
    const { onAnswer } = props;
    const [buttonRef, animateButton] = useAnimate();
    const [noteRef, animateNote] = useAnimate();
    const { enableAccentuation, enableJumpLine } = useDictationConfig();
    const [hasAnswered, setHasAnswered] = useState(false);
    const questions = useSelector(state => state.game.questions);
    const currentQuestion = useSelector(state => questions[state.game.currentIndex]);
    const correctAnswerQuestionArray = currentQuestion.correctAnswers[0];
    const [originalTextArray, setOriginalTextArray] = useState([]);
    const isTouchDevice = useMediaQuery("(hover:none)") || ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0);
    const [focusNote, setFocusNote] = useState(false);
    const t = useTranslate();
    const gameSounds = useGameSounds();
    const [corrections, setCorrections] = useState(null);
    const visualFeedbackRef = useRef();
    const dispatch = useDispatch();
    const isAnimating = useSelector(state => state.game.isAnimating);

    useImperativeHandle(ref, () => ({
        animateEntry: async () => {
            setTimeout(() => {
                gameSounds.play(DICTADO_SOUNDS.MOVE);
            }, DELAY_SOUND_CHARGE_NOTE);
            await animateNote(noteRef.current, { y: 0 }, { duration: .5, delay: 0.3, type: "spring" });
            setTimeout(() => {
                gameSounds.play(DICTADO_SOUNDS.GOOD_ACTIVE);
            }, DELAY_SOUND_CHARGE_BUTTON);
            await animateButton(buttonRef.current, { y: 0 }, { duration: .5, type: "spring" });
        },

        animateExit: async () => {
            setTimeout(() => {
                gameSounds.play(DICTADO_SOUNDS.MOVE);
            }, DELAY_SOUND_CHARGE_NOTE);
            await animateNote(noteRef.current, { y: "100%" }, { duration: .5, type: "spring" });
        }
    }));

    useEffect(() => {
        const reproduceAnimations = async () => {
            await ref.current.animateExit();
            ref.current.animateEntry();
        };

        setCorrections(null);
        setFocusNote(false);
        setOriginalTextArray([]);
        if (currentQuestion.id > 0) reproduceAnimations();

    }, [currentQuestion.id, ref]);

    const checkAnswer = async () => {
        if (isAnimating) return;
        dispatch(setAnimating(true));

        gameSounds.play(DICTADO_SOUNDS.ACTIVE);
        const result = getCorrection({
            textEntry: originalTextArray,
            textExpected: correctAnswerQuestionArray,
            ignoreJumpline: !enableJumpLine,
            keySensitive: enableAccentuation
        });

        const isCorrect = result.flat().every((item) => item.isCorrect);
        const duration = isCorrect ? 100 : 200;

        if (!isCorrect) gameSounds.play(DICTADO_SOUNDS.DRAG);
        await visualFeedbackRef.current?.show({ isCorrect, duration });

        await wait(300);

        animateButton(buttonRef.current, { y: "200%" }, { duration: .5, type: "spring" });
        const filteredResult = result.map(line => line.filter((line) => !(line.expected === "" && line.userAnswer === "")));
        setCorrections(filteredResult);
    };

    const handleFinish = () => {
        dispatch(setAnimating(false));
        setHasAnswered(true);
    }

    //created useEffect to run the onAnswer function with the Animating state updated to false
    useEffect(() => {
        if (hasAnswered) {
            const userAnswer = originalTextArray.join(' ');
            const answer = {
                userAnswer,
                corrections,
            }
            onAnswer(answer, { playErrorSound: false });
            ref.current.animateExit();
            setHasAnswered(false);
        }
    }, [hasAnswered, onAnswer, corrections, ref, originalTextArray]);

    return (
        <>
            <Fill name={"core-bank-right"}>
                {t("specific.screen")}: {currentQuestion.id + 1}/{questions.length}
            </Fill>

            <motion.div
                ref={noteRef}
                initial={{ y: "100%" }}
                style={{ position: "absolute", inset: 0 }}
                transition={{ duration: 1, type: "spring" }}
            >
                {corrections && (
                    <CorrectionSummary
                        corrections={corrections}
                        onFinish={handleFinish}
                        animated
                    />
                )}

                {!corrections && (
                    <TextInput
                        focusNote={focusNote}
                        onChange={(originalText) => setOriginalTextArray(formatArrayText(originalText, enableAccentuation))}
                        onFocus={() => {
                            if (!isTouchDevice) return;
                            setFocusNote(true);
                        }}
                        onBlur={() => {
                            if (!isTouchDevice) return;
                            setFocusNote(false);
                        }}
                    />
                )}
            </motion.div>

            <VisualFeedback ref={visualFeedbackRef} />
            <CheckButton onClick={checkAnswer} focusNote={focusNote} ref={buttonRef} />
        </>
    )
})